import { notifyError } from './toast.js';
import { adminMfaCodeFrom } from './services/adminMfaGate.js';

// Shown when a write may or may not have landed (timeout, lost response).
export const UNCONFIRMED_CHANGE = 'Could not confirm the change. Refresh to check before trying again.';

// Status labels and transport strings that mean nothing to an admin. The API
// often answers { error: 'Forbidden', message: '<the real sentence>' }.
const RAW_ERROR_TEXT = [
  /^(forbidden|unauthorized|bad request|not found|internal server error|network error|failed to fetch)$/i,
  /^request failed with status code \d+$/i,
  /^timeout of \d+ms exceeded$/i,
];

const isReadable = (text) => {
  return typeof text === 'string' && Boolean(text.trim())
    && !RAW_ERROR_TEXT.some((pattern) => { return pattern.test(text.trim()); });
};

/**
 * The best human sentence for a failed request: the server's error, then its
 * message, then the thrown message, then the caller's fallback.
 *
 * @param {any} err
 * @param {string} fallback
 * @returns {string}
 */
export const readableError = (err, fallback) => {
  const data = err?.response?.data || {};
  return [data.error, data.message, err?.message].find(isReadable) || fallback;
};

// A 403 left by a cancelled two-factor prompt was the admin's choice, so the
// caller should not toast it.
export const isCancelledStepUp = (err) => { return Boolean(adminMfaCodeFrom(err)); };

/**
 * Toast a failed admin write. Silent for a cancelled step-up and for a network
 * drop (the api interceptor already shows the network toast). The fallback may
 * promise "Nothing was changed.", so it is only used when the server answered;
 * with no response the change may have landed, and the admin is told to check.
 *
 * @param {any} err
 * @param {string} fallback
 */
export const notifyActionError = (err, fallback, unconfirmed = UNCONFIRMED_CHANGE) => {
  if (isCancelledStepUp(err)) { return; }
  if (!err?.response) {
    if (err?.code === 'ERR_NETWORK') { return; }
    notifyError(unconfirmed);
    return;
  }
  notifyError(readableError(err, fallback));
};

/**
 * Toast a failed request that changes nothing (a chat turn, a read). Silent for
 * a cancelled step-up; otherwise the most readable sentence available.
 *
 * @param {any} err
 * @param {string} fallback
 */
export const notifyRequestError = (err, fallback) => {
  if (isCancelledStepUp(err)) { return; }
  notifyError(readableError(err, fallback));
};
